import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

declare let grecaptcha: any;

/**
 * Google reCAPTCHA v3.
 *
 * v3 shows no puzzle and no checkbox. The browser asks Google for a token just
 * before a form is submitted, the token rides along with the request, and the
 * backend asks Google how human that visit looked. Nothing is decided here —
 * this class only fetches the token; App\Http\Middleware\VerifyRecaptcha does
 * the judging, because a score the browser reports is a score an attacker can
 * write.
 *
 * Deliberately no npm package. ng-recaptcha's v3 support would mean a new
 * dependency and a module import for what is one script tag and one call, and
 * the widget components it exists for are a v2 concern.
 *
 * The script is loaded once, lazily, and shared: Google's badge is global, so a
 * second copy would stack a second badge in the corner.
 */
@Injectable({ providedIn: 'root' })
export class RecaptchaService {
    /*
     * Actions. Google records these per token and the backend checks the one it
     * gets against the one the route expects, so a token minted on some other
     * form cannot be replayed against sign-in. They MUST match the arguments in
     * routes ('recaptcha:login', 'recaptcha:register').
     */
    static readonly LOGIN = 'login';
    static readonly REGISTER = 'register';

    private loader: Promise<any> = null;

    get siteKey(): string {
        return environment.recaptchaSiteKey;
    }

    /** No key configured means the whole thing is off — see execute(). */
    get enabled(): boolean {
        return !!this.siteKey;
    }

    /**
     * Start fetching the script early.
     *
     * Called when a sign-in surface opens so the token is ready by the time the
     * customer has finished typing, instead of adding a network round trip to
     * the click. Failures are swallowed: this is a head start, not a step, and
     * execute() will try again on its own.
     */
    preload(): void {
        if (this.enabled) {
            this.load().catch(() => { });
        }
    }

    /**
     * A fresh token for one submission.
     *
     * Tokens are single-use and expire after about two minutes, so this is
     * called per request rather than cached.
     *
     * Resolves to '' when no key is configured or the script cannot be reached.
     * That empty token is then rejected by the backend, which is intended: the
     * captcha is a gate, and a gate that opens whenever it breaks is not one.
     * To switch the check off, turn RECAPTCHA_ENABLED off on the server.
     */
    execute(action: string): Promise<string> {
        if (!this.enabled) {
            return Promise.resolve('');
        }

        return this.load()
            .then(api => api.execute(this.siteKey, { action }))
            .catch(() => '');
    }

    private load(): Promise<any> {
        if (this.loader) {
            return this.loader;
        }

        this.loader = new Promise((resolve, reject) => {
            if (typeof grecaptcha !== 'undefined' && grecaptcha.execute) {
                resolve(grecaptcha);
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://www.google.com/recaptcha/api.js?render=' + this.siteKey;
            script.async = true;
            script.defer = true;
            // grecaptcha.ready() rather than onload alone: the script is present
            // before its internals finish wiring up, and calling execute() in
            // that gap throws.
            script.onload = () => grecaptcha.ready(() => resolve(grecaptcha));
            script.onerror = () => {
                // Cleared so a later attempt can retry — a cached rejected
                // promise would keep failing for the rest of the session.
                this.loader = null;
                reject(new Error('reCAPTCHA script could not be loaded'));
            };

            document.head.appendChild(script);
        });

        return this.loader;
    }
}
